import { Box, Tab, useMediaQuery, useTheme } from "@mui/material";
import { useState, useMemo } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useSelector } from "react-redux";
import FeedbackList from "./feedback/FeedbackList";
import FeedbackView from "./feedback/FeedbackView";

const UserFeedback = ({
  onSwitchTab,
  onSetMessageRecipient,
  onSetConversationToView,
}) => {
  const [activeTab, setActiveTab] = useState("bug-report");
  const [selectedBugReportId, setSelectedBugReportId] = useState(null);
  const [selectedFeatureRequestId, setSelectedFeatureRequestId] =
    useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { feedback = [] } = useSelector((state) => state.admin);

  // Count unread for badges
  const unreadBugs = useMemo(() => {
    return feedback.filter((f) => f.feedbackType === "bug-report" && !f.read)
      .length;
  }, [feedback]);

  const unreadFeatures = useMemo(() => {
    return feedback.filter(
      (f) => f.feedbackType === "feature-request" && !f.read,
    ).length;
  }, [feedback]);

  const handleSwitchToMessaging = (userId) => {
    if (onSwitchTab && onSetConversationToView) {
      onSwitchTab("6"); // Messaging tab
      onSetConversationToView(userId);
    }
  };

  const handleSelectBugReport = (id) => {
    setSelectedBugReportId(id);
  };

  const handleSelectFeatureRequest = (id) => {
    setSelectedFeatureRequestId(id);
  };

  return (
    <Box>
      <TabContext value={activeTab}>
        <TabList onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab
            label={`Bug Reports${unreadBugs > 0 ? ` (${unreadBugs})` : ""}`}
            value="bug-report"
          />
          <Tab
            label={`Feature Requests${unreadFeatures > 0 ? ` (${unreadFeatures})` : ""}`}
            value="feature-request"
          />
        </TabList>

        <TabPanel value="bug-report" sx={{ p: 0, pt: 2 }}>
          {isMobile ? (
            // Mobile: Show either list or detail view
            selectedBugReportId ? (
              <Box sx={{ height: "600px" }}>
                <FeedbackView
                  feedbackId={selectedBugReportId}
                  feedbackType="bug-report"
                  onBack={() => setSelectedBugReportId(null)}
                  onSwitchToMessaging={handleSwitchToMessaging}
                />
              </Box>
            ) : (
              <Box sx={{ height: "600px" }}>
                <FeedbackList
                  feedbackType="bug-report"
                  onSelectFeedback={handleSelectBugReport}
                  selectedFeedbackId={selectedBugReportId}
                />
              </Box>
            )
          ) : (
            // Desktop: Split view
            <Box sx={{ display: "flex", gap: 2, height: "600px" }}>
              <Box
                sx={{
                  width: { sm: "300px", md: "350px", lg: "400px" },
                  flexShrink: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <FeedbackList
                  feedbackType="bug-report"
                  onSelectFeedback={handleSelectBugReport}
                  selectedFeedbackId={selectedBugReportId}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <FeedbackView
                  feedbackId={selectedBugReportId}
                  feedbackType="bug-report"
                  onSwitchToMessaging={handleSwitchToMessaging}
                />
              </Box>
            </Box>
          )}
        </TabPanel>

        <TabPanel value="feature-request" sx={{ p: 0, pt: 2 }}>
          {isMobile ? (
            // Mobile: Show either list or detail view
            selectedFeatureRequestId ? (
              <Box sx={{ height: "600px" }}>
                <FeedbackView
                  feedbackId={selectedFeatureRequestId}
                  feedbackType="feature-request"
                  onBack={() => setSelectedFeatureRequestId(null)}
                  onSwitchToMessaging={handleSwitchToMessaging}
                />
              </Box>
            ) : (
              <Box sx={{ height: "600px" }}>
                <FeedbackList
                  feedbackType="feature-request"
                  onSelectFeedback={handleSelectFeatureRequest}
                  selectedFeedbackId={selectedFeatureRequestId}
                />
              </Box>
            )
          ) : (
            // Desktop: Split view
            <Box sx={{ display: "flex", gap: 2, height: "600px" }}>
              <Box
                sx={{
                  width: { sm: "300px", md: "350px", lg: "400px" },
                  flexShrink: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <FeedbackList
                  feedbackType="feature-request"
                  onSelectFeedback={handleSelectFeatureRequest}
                  selectedFeedbackId={selectedFeatureRequestId}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <FeedbackView
                  feedbackId={selectedFeatureRequestId}
                  feedbackType="feature-request"
                  onSwitchToMessaging={handleSwitchToMessaging}
                />
              </Box>
            </Box>
          )}
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default UserFeedback;
